import { UIEvent, useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useIsSmallScreen from '@hooks/element-size-hooks/useIsSmallScreen';
import { selectedDateWithoutTime } from '@store/global/selectors';
import { setCustomersAndPagingInfo, resetOrdersAndCustomersStore } from '@pages/orders/store/slice';
import { selectCustomersPagingInfo } from '@pages/orders/store/selectors';
import { PagingCustomersData } from '@api/shipping-data/types/PagingCustomersData';
import { useLazyGetCustomersQuery } from '@api/shipping-data/ordersApi';
import { useResetCustomerOrdersMutation, useResetCustomerInfoMutation, useResetOrderTimelineMutation } from '@api/shipping-data/detailApi';
import { useLazyDayOrdersStatisticsSummaryQuery, useResetDayOrdersStatisticsSummaryMutation } from '@api/shipping-data/api';
import { AppPage } from 'components/app-page/AppPage';
import Statistics from '@pages/orders/statistics/Statistics';
import ListArea from '@pages/orders/customers-page/list-area/ListArea';
import Detail from '@pages/orders/detail/Detail';



export const Customers = () => {

	const pageRef = useRef<HTMLDivElement>(null);
	const isSmallScreen = useIsSmallScreen();
	const dateWithoutTime = useSelector(selectedDateWithoutTime);
	const pagingInfo = useSelector(selectCustomersPagingInfo);

	const [ customersScrollTop, setCustomersScrollTop ] = useState(0);
	const [ customersTop, setCustomersTop ] = useState(0);
	const [ customersHeight, setCustomersHeight ] = useState(0);
	const [ isScrolledDown, setIsScrolledDown ] = useState(false);

	const dispatch = useDispatch();
	const updateCustomersAndPagingInfo = (pagingCustomersData: PagingCustomersData) => dispatch(setCustomersAndPagingInfo(pagingCustomersData));
	const dispatchResetCustomersStore = () => dispatch(resetOrdersAndCustomersStore());

	const [ getCustomers, { data, isLoading, isFetching, isError } ] = useLazyGetCustomersQuery();
	const [ getStatisticsSummary, { data: stData, isLoading: stIsLoading, isFetching: stIsFetching, isError: stIsError } ] = useLazyDayOrdersStatisticsSummaryQuery();
	const [ resetStatistics ] = useResetDayOrdersStatisticsSummaryMutation();
	const [ resetCustomerOrders ] = useResetCustomerOrdersMutation();
	const [ resetCustomerInfo ] = useResetCustomerInfoMutation();
	const [ resetOrderTimeline ] = useResetOrderTimelineMutation();


	useEffect(() => {
		if (pageRef && pageRef.current) {
			// init scroll values, before scroll event is triggered
			setCustomersTop(pageRef.current.getBoundingClientRect().top);
			setCustomersHeight(pageRef.current.clientHeight);
		}
		return () => {
			dispatchResetCustomersStore();
		}
	}, []);
	
	
	useEffect(() => {
		if (dateWithoutTime) {
			getCustomersInit();
			getStatisticsSummary(dateWithoutTime, true);
		}
	}, [ dateWithoutTime ]);


	useEffect(() => {
		if (isScrolledDown) {
			getCustomersNextPage();
		}
	}, [ isScrolledDown ]);


	const getCustomersInit = useCallback(() => {
		if (dateWithoutTime) {
			getCustomers({ date: dateWithoutTime });
		}
	},[ dateWithoutTime ]);


	const getCustomersNextPage = useCallback(() => {
		if (dateWithoutTime && pagingInfo && pagingInfo.hasNextPage && pagingInfo.continuationToken) {
			getCustomers({ date: dateWithoutTime, continuationToken: pagingInfo.continuationToken });
		}
	},[ dateWithoutTime, pagingInfo ]);


	useEffect(() => {
		if (data) {
			updateCustomersAndPagingInfo(data);
		}
	}, [ data ]);

	
	const resetAllApi = useCallback(() => {
		getCustomersInit();
		resetStatistics();
		resetCustomerOrders();
		resetCustomerInfo();
		resetOrderTimeline();
	},[ getCustomersInit ]);


	return (
		<AppPage
			isError={isError || stIsError}
			isLoading={isLoading || stIsLoading || stIsFetching}
			className='customers-page'
			pageLabel='Zákazníci'
			resetPageApi={resetAllApi}
			ref={pageRef}
			onScroll={(event: UIEvent<HTMLDivElement>) => {
				const bottom = event.currentTarget.scrollHeight - event.currentTarget.scrollTop < event.currentTarget.clientHeight + 400;
				setIsScrolledDown(bottom);
				setCustomersScrollTop(event.currentTarget.scrollTop);
				setCustomersTop(event.currentTarget.getBoundingClientRect().top);
				setCustomersHeight(event.currentTarget.clientHeight);
			}}
		>
			<div className='customers-content'>
				
				<Statistics statistics={stData} />
				
				<ListArea isSmallScreen={isSmallScreen} pagingLoading={data && data.pagingInfo.hasNextPage && isFetching ? true : false} />
				
				{!isSmallScreen &&
					<Detail
						dateWithoutTime={dateWithoutTime}
						isSmallScreen={isSmallScreen}
						ordersScrollTop={customersScrollTop}
						ordersTop={customersTop}
						ordersHeight={customersHeight} />}
			</div>
		
		</AppPage>
	)
}

export default Customers;